"use client";

import { MinusIcon, PlusIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { useSearchParams } from "next/navigation";
import { useForm } from "react-hook-form";

import { useAddProduct } from "../hooks/use-add-to-cart";

import {
  ConfigurableProductData,
  ConfigurableProductIndexData,
} from "@/lib/bagisto/types";
import LoadingDots from "@/components/loading-dots";
import { Button } from "../ui/button";
import { CartIcon } from "@/components/icons/assets";

function SubmitButton({
  availableForSale,
  selectedVariantId,
  pending,
}: {
  availableForSale: boolean;
  selectedVariantId: boolean;
  pending: boolean;
}) {
  const buttonClasses =
    "relative w-full h-[60px] bg-primary-500 hover:bg-primary-500 text-white px-5 py-2.5 rounded-[12px] font-oswald font-medium text-[18px] leading-[100%] tracking-[0.2px] uppercase cursor-pointer flex items-center justify-center gap-2";
  const disabledClasses = "cursor-wait";

  if (!availableForSale) {
    return (
      <Button aria-disabled className={clsx(buttonClasses, disabledClasses)}>
        Out Of Stock
      </Button>
    );
  }

  if (!selectedVariantId) {
    return (
      <Button
        aria-disabled
        aria-label="Please select an option"
        disabled={!selectedVariantId}
        className={clsx(buttonClasses, " opacity-60 !cursor-not-allowed")}
      >
        Add To Cart
      </Button>
    );
  }

  return (
    <Button
      aria-disabled={pending}
      aria-label="Add to cart"
      className={clsx(buttonClasses, {
        "": true,
        [disabledClasses]: pending,
      })}
      onClick={(e: React.FormEvent<HTMLButtonElement>) => {
        if (pending) e.preventDefault();
      }}
    >
      {pending ? <LoadingDots className="mb-3 bg-white" /> : (
        <>
          Add To Cart
          <CartIcon className="!h-5 !w-5" />
        </>
      )}
    </Button>
  );
}

export function AddToCart({
  variants,
  availableForSale,
  index,
  productId,
}: {
  variants: ConfigurableProductData[];
  availableForSale: boolean;
  productId: string;
  index: ConfigurableProductIndexData[];
}) {
  const { onAddToCart, isCartLoading } = useAddProduct();

  // ✅ useForm controls quantity now
  const { handleSubmit, setValue, watch, register } = useForm({
    defaultValues: {
      quantity: 1,
      isBuyNow: false,
    },
  });

  // Watch quantity so UI updates
  const quantity = watch("quantity");

  const increment = () => setValue("quantity", quantity + 1);
  const decrement = () => setValue("quantity", Math.max(1, quantity - 1));

  const searchParams = useSearchParams();
  // Function to convert URLSearchParams to object
  const searchParamsToObject = (searchParams: any) => {
    const paramsObject: any = {};

    for (const [key, value] of searchParams.entries()) {
      paramsObject[key] = value;
    }

    return paramsObject;
  };

  // Convert searchParams to object
  const searchParamsObject = searchParamsToObject(searchParams);

  const findMatchingObject = (
    searchParamsObject: any,
    index: ConfigurableProductIndexData[]
  ) => {
    for (const data of index) {
      let match = true;
      const attributeOptionIds = [];

      for (const attributeOption of data.attributeOptionIds) {
        const attributeCode = attributeOption.attributeCode;
        const attributeOptionId = attributeOption.attributeOptionId;

        if (searchParamsObject[attributeCode] !== attributeOptionId) {
          match = false;
          break;
        }
        attributeOptionIds.push({
          attributeId: Number(attributeOption.attributeId),
          attributeOptionId: Number(attributeOption.attributeOptionId),
        });
      }
      if (match) {
        return { ...data, attributeOptionIds };
      }
    }

    return null;
  };

  const matchingObject = findMatchingObject(searchParamsObject, index);

  const defaultVariantId = variants.length === 1 ? variants[0]?.id : productId;
  const buttonStatus =
    variants.length >= 1 ? (matchingObject?.id ? true : false) : true;

  const variant = variants.find((variant) => {
    const selectedOptionId = searchParams.get(variant.code.toLowerCase());

    return variant.options.find((option) => option.id === selectedOptionId);
  });

  const selectedVariantId = matchingObject?.id || defaultVariantId;
  const selectedConfigurableOption = Number(
    Object.keys(searchParamsObject).length
      ? matchingObject
        ? matchingObject?.id
        : ""
      : variant?.id || defaultVariantId
  );
  const superAttribute = matchingObject?.attributeOptionIds || [];

  const actionWithVariant = async (data: {
    quantity: number;
    isBuyNow: boolean;
  }) => {
    onAddToCart({
      input: {
        quantity: data?.quantity,
        isBuyNow: data?.isBuyNow,
        productId: selectedVariantId || "",
        selectedConfigurableOption,
        superAttribute,
      },
    });
  };

  return (
    <form className="flex flex-col gap-10" onSubmit={handleSubmit(actionWithVariant)}>
      
      {/* Quantity */}
      <div className="flex flex-col gap-4 xl:h-[99px]">
        <span className="font-oswald font-medium text-base leading-[100%] tracking-[0.2px] uppercase text-neutral-500 xl:h-7">
          Quantity
        </span>
        <div className="w-full h-14 bg-background-50 rounded-[12px] px-3 py-2 flex items-center gap-2">
          <button
            aria-label="Decrease quantity"
            type="button"
            onClick={decrement}
            className="w-10 h-10 bg-neutral-500 rounded-[8px] flex items-center justify-center cursor-pointer flex-shrink-0"
          >
            <MinusIcon className="h-6 w-6 text-white" />
          </button>
          
          <input
            type="hidden"
            {...register("quantity", { valueAsNumber: true })}
          />
          
          <div className="flex-1 flex items-center justify-center font-oswald font-medium text-lg leading-[100%] text-neutral-500">
            {String(quantity).padStart(2, '0')}
          </div>
          
          <button
            aria-label="Increase quantity"
            type="button"
            onClick={increment}
            className="w-10 h-10 bg-neutral-500 rounded-[8px] flex items-center justify-center cursor-pointer flex-shrink-0"
          >
            <PlusIcon className="h-6 w-6 text-white" />
          </button>
        </div>
      </div>

      {/* Add to Cart Button */}
      <SubmitButton
        availableForSale={availableForSale}
        pending={isCartLoading}
        selectedVariantId={buttonStatus}
      />
      
    </form>
  );
}
